/**
 * @fileoverview Define shared timezone-aware event membership, overlap, and display semantics for calendar views. Month/week/day/agenda layouts consume the same rules to agree about which events belong in a visible period. Backend event state remains authoritative; these helpers position and label records without changing their timing or assignments.
 */

import moment from 'moment-timezone';





export const parseEventDate = (isoString, allDay) => {
  if (allDay) return moment(isoString, 'YYYY-MM-DD');
  return moment(isoString);
};




// Calendar view types
export const CALENDAR_VIEWS = {
  MONTH_GRID: 'month-grid',
  MONTH_AGENDA: 'month-agenda',
  WEEK: 'week',
  DAY: 'day'
};

// Timed event cards are rendered at a minimum height of 30px in the day and
// week grids (one pixel per minute). Collision detection must use the same
// minimum duration or zero/short-duration events can be placed underneath a
// neighboring card even though their rendered rectangles overlap.
export const MIN_TIMED_EVENT_DURATION_MINUTES = 30;





export const generateMonthGrid = (date) => {
  const startOfMonth = moment(date).startOf('month');
  const startOfCalendar = moment(startOfMonth).startOf('week');
  
  const grid = [];
  let currentDate = moment(startOfCalendar);
  
  // Generate 6 weeks (42 days) to accommodate all possible month layouts
  for (let week = 0; week < 6; week++) {
    const weekDays = [];
    for (let day = 0; day < 7; day++) {
      weekDays.push(moment(currentDate));
      currentDate.add(1, 'day');
    }
    grid.push(weekDays);
  }
  
  return grid;
};





export const generateWeekDays = (date) => {
  const startOfWeek = moment(date).startOf('week');
  const days = [];
  
  for (let i = 0; i < 7; i++) {
    days.push(moment(startOfWeek).add(i, 'days'));
  }
  
  return days;
};





export const generateHourSlots = () => {
  const hours = [];
  const base = moment().startOf('day');
  for (let i = 0; i < 24; i++) {
    hours.push(base.clone().add(i, 'hour'));
  }
  return hours;
};





export const isDateInCurrentMonth = (date, viewingMonth) => {
  return date.isSame(viewingMonth, 'month');
};





export const isToday = (date) => {
  return date.isSame(moment(), 'day');
};





export const calculateEventPosition = (event, viewStart, viewEnd, viewType) => {
  const eventStart = parseEventDate(event.start, event.allDay);
  const eventEnd = parseEventDate(event.end, event.allDay);
  
  // Ensure event overlaps with view period
  if (eventEnd.isBefore(viewStart) || eventStart.isAfter(viewEnd)) {
    return null;
  }
  
  const clampedStart = moment.max(eventStart, viewStart);
  const clampedEnd = moment.min(eventEnd, viewEnd);
  
  switch (viewType) {
    case CALENDAR_VIEWS.MONTH_GRID:
      return calculateMonthGridPosition(clampedStart, clampedEnd, viewStart);
    case CALENDAR_VIEWS.WEEK:
      return calculateWeekPosition(clampedStart, clampedEnd, viewStart);
    case CALENDAR_VIEWS.DAY:
      return calculateDayPosition(clampedStart, clampedEnd);
    default:
      return null;
  }
};



const calculateMonthGridPosition = (start, end, viewStart) => {
  const startDay = start.diff(viewStart, 'days');
  const duration = end.diff(start, 'days') + 1;
  
  return {
    startDay,
    duration,
    startWeek: Math.floor(startDay / 7),
    startDayOfWeek: startDay % 7,
    spansMultipleWeeks: duration > (7 - (startDay % 7))
  };
};



const calculateWeekPosition = (start, end, viewStart) => {
  const startDay = start.diff(viewStart, 'days');
  const startHour = start.hour() + (start.minute() / 60);
  const endHour = end.hour() + (end.minute() / 60);
  const duration = end.diff(start, 'hours', true);
  
  return {
    dayIndex: startDay,
    startHour,
    endHour,
    duration,
    top: (startHour * 60), // Minutes from midnight
    height: Math.max(duration * 60, 30) // Minimum 30 minutes
  };
};



const calculateDayPosition = (start, end) => {
  const startHour = start.hour() + (start.minute() / 60);
  const endHour = end.hour() + (end.minute() / 60);
  const duration = end.diff(start, 'hours', true);
  
  return {
    startHour,
    endHour,
    duration,
    top: (startHour * 60), // Minutes from midnight
    height: Math.max(duration * 60, 30) // Minimum 30 minutes
  };
};





export const groupEventsByDay = (events, startDate, endDate) => {
  const groupedEvents = {};
  
  events.forEach(

  event => {
    const eventStart = parseEventDate(event.start, event.allDay);
    const eventEnd = parseEventDate(event.end, event.allDay);

    // Handle multi-day events by creating entries for each day.
    // Clone the result of moment.max/min to avoid mutating the original
    // startDate/endDate references (which are shared grid moment objects).
    let currentDay = moment(moment.max(eventStart, startDate)).startOf('day');
    const lastDay = moment(moment.min(eventEnd, endDate)).startOf('day');
    
    while (currentDay.isSameOrBefore(lastDay)) {
      const dayKey = currentDay.format('YYYY-MM-DD');
      
      if (!groupedEvents[dayKey]) {
        groupedEvents[dayKey] = [];
      }
      
      groupedEvents[dayKey].push({
        ...event,
        isMultiDay: !eventStart.isSame(eventEnd, 'day'),
        isFirstDay: currentDay.isSame(eventStart, 'day'),
        isLastDay: currentDay.isSame(eventEnd, 'day')
      });
      
      currentDay.add(1, 'day');
    }
  });
  
  return groupedEvents;
};





export const calculateEventOverlaps = (events) => {
  if (events.length === 0) return [];

  const eventRanges = events.map(

  (event) => {
    const start = moment(event.start);
    const suppliedEnd = moment(event.end);
    const minimumEnd = start.clone().add(MIN_TIMED_EVENT_DURATION_MINUTES, 'minutes');
    const end = suppliedEnd.isValid() && suppliedEnd.isAfter(minimumEnd)
      ? suppliedEnd
      : minimumEnd;

    return { event, start, end };
  });
  
  // Sort events by start time, then by duration (longer events first)
  const sortedEventRanges = eventRanges.sort(

  (a, b) => {
    const startDiff = a.start.valueOf() - b.start.valueOf();
    if (startDiff !== 0) return startDiff;
    // If same start time, longer events come first
    const aDuration = a.end.valueOf() - a.start.valueOf();
    const bDuration = b.end.valueOf() - b.start.valueOf();
    return bDuration - aDuration;
  });
  
  // Helper function to check if two events truly overlap (not just touch at boundaries)
  const doEventsOverlap =

  (event1, event2) => {
    // True overlap requires that one starts before the other ends
    // AND they don't just touch at the boundary
    return event1.start.isBefore(event2.end) && event2.start.isBefore(event1.end);
  };
  
  // Track which column each event is assigned to
  const eventColumns = [];
  
  // Process each event and assign it to a column
  sortedEventRanges.forEach(

  (event, eventIndex) => {
    // Find all events that overlap with this one
    const overlappingEvents = [];
    for (let i = 0; i < eventIndex; i++) {
      if (doEventsOverlap(sortedEventRanges[i], event)) {
        overlappingEvents.push(i);
      }
    }
    
    // Find the leftmost available column
    // Start by finding which columns are already occupied by overlapping events
    const occupiedColumns = new Set(
      overlappingEvents.map(i => eventColumns[i])
    );
    
    // Assign to the first available column (starting from 0)
    let assignedColumn = 0;
    while (occupiedColumns.has(assignedColumn)) {
      assignedColumn++;
    }
    
    eventColumns[eventIndex] = assignedColumn;
  });
  
  // Calculate the maximum number of concurrent events for each event
  // This determines how many columns we need in each event's time slice
  const eventsWithOverlaps = sortedEventRanges.map(

  (eventRange, eventIndex) => {
    // Find all events that overlap with this one (including itself)
    const overlappingIndices = [eventIndex];
    for (let i = 0; i < sortedEventRanges.length; i++) {
      if (i !== eventIndex && doEventsOverlap(sortedEventRanges[i], eventRange)) {
        overlappingIndices.push(i);
      }
    }
    
    // The total columns needed is the max column number + 1
    const maxColumn = Math.max(...overlappingIndices.map(i => eventColumns[i]));
    const totalColumns = maxColumn + 1;
    
    return {
      ...eventRange.event,
      overlapIndex: eventColumns[eventIndex],
      totalOverlapping: totalColumns
    };
  });
  
  return eventsWithOverlaps;
};





export const formatTime = (time, use24Hour = true) => {
  const momentTime = moment.isMoment(time)
    ? time
    : typeof time === 'string' && /^\d{2}:\d{2}$/.test(time)
      ? moment(time, 'HH:mm')
      : moment(time);
  return use24Hour ? momentTime.format('HH:mm') : momentTime.format('h:mm A');
};





export const formatDate = (date, format = 'YYYY-MM-DD') => {
  return moment(date).format(format);
};



export const navigation = {
  previousMonth: (currentDate) => moment(currentDate).subtract(1, 'month'),
  nextMonth: (currentDate) => moment(currentDate).add(1, 'month'),
  previousWeek: (currentDate) => moment(currentDate).subtract(1, 'week'),
  nextWeek: (currentDate) => moment(currentDate).add(1, 'week'),
  previousDay: (currentDate) => moment(currentDate).subtract(1, 'day'),
  nextDay: (currentDate) => moment(currentDate).add(1, 'day'),
  today: () => moment(),
  
  getNavigationLabel: (date, viewType, isMobile = false) => {
    switch (viewType) {
      case CALENDAR_VIEWS.MONTH_GRID:
      case CALENDAR_VIEWS.MONTH_AGENDA:
        return date.format(isMobile ? 'MMM YYYY' : 'MMMM YYYY');
      case CALENDAR_VIEWS.WEEK:
        const weekStart = moment(date).startOf('week');
        const weekEnd = moment(date).endOf('week');
        if (isMobile) {
          return `${weekStart.format('MMM D')} - ${weekEnd.format('D')}`;
        }
        return `${weekStart.format('MMMM D')} - ${weekEnd.format('MMMM D, YYYY')}`;
      case CALENDAR_VIEWS.DAY:
        return date.format(isMobile ? 'ddd, MMM D' : 'dddd, MMMM D, YYYY');
      default:
        return date.format('MMMM YYYY');
    }
  }
};



export const colorUtils = {
  lightenColor:

  (color, amount) => {
    const colorInt = parseInt(color.slice(1), 16);
    const r = Math.min(255, (colorInt >> 16) + amount);
    const g = Math.min(255, ((colorInt >> 8) & 0x00FF) + amount);
    const b = Math.min(255, (colorInt & 0x0000FF) + amount);
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  },
  
  darkenColor: (color, amount) => {
    return colorUtils.lightenColor(color, -amount);
  },
  
  getContrastColor:

  (hexColor) => {
    const r = parseInt(hexColor.substr(1, 2), 16);
    const g = parseInt(hexColor.substr(3, 2), 16);
    const b = parseInt(hexColor.substr(5, 2), 16);
    const luminance = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return luminance >= 128 ? '#000000' : '#ffffff';
  }
};
